import React, { useState } from 'react';
import { X, Save, Loader2, Clock, Users, Euro, StickyNote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompletedServiceEditModal = ({ appointment, staff, hours, token, language, onClose, onSaved }) => {
  const pt = language === 'pt';
  const initialIds = [...new Set([...(appointment.assigned_staff_ids || []), ...(appointment.assigned_staff_id ? [appointment.assigned_staff_id] : [])])];
  const [staffIds, setStaffIds] = useState(initialIds);
  const [h, setH] = useState(String(Math.floor(hours || 0)));
  const [m, setM] = useState(String(Math.round(((hours || 0) % 1) * 60)));
  const [price, setPrice] = useState(appointment.estimated_price ?? '');
  const [staffCost, setStaffCost] = useState(appointment.staff_cost_override ?? '');
  const [notes, setNotes] = useState(appointment.notes || appointment.message || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggle = (id) => setStaffIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const totalHours = (parseInt(h, 10) || 0) + (parseInt(m, 10) || 0) / 60;

  const save = async () => {
    setBusy(true); setError('');
    try {
      await axios.put(`${API}/admin/appointments/${appointment.id}/completed-edit`, {
        staff_ids: staffIds, hours: Math.round(totalHours * 100) / 100,
        estimated_price: price === '' ? null : parseFloat(price), notes,
        staff_cost: staffCost === '' ? null : parseFloat(staffCost), clear_staff_cost: staffCost === '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved?.(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : (pt ? 'Erro ao guardar.' : 'Failed to save.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[55] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="completed-edit-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-semibold text-gray-900">{pt ? 'Editar serviço concluído' : 'Edit completed service'}</h2>
            <p className="text-xs text-gray-500 truncate">{appointment.name} · {appointment.service_type}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100" data-testid="completed-edit-close"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div>
            <p className="text-xs text-gray-600 flex items-center gap-1 mb-2"><Users size={12} /> {pt ? 'Funcionários' : 'Staff'}</p>
            <div className="flex flex-wrap gap-2" data-testid="completed-edit-staff">
              {staff.filter(s => s.is_active !== false || staffIds.includes(s.id)).map(s => (
                <button key={s.id} type="button" onClick={() => toggle(s.id)} data-testid={`completed-edit-staff-${s.id}`}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${staffIds.includes(s.id) ? 'bg-[#1a5c3a] text-white border-[#1a5c3a]' : 'text-gray-700 hover:bg-gray-50'}`}>{s.name}</button>
              ))}
            </div>
            {staffIds.length === 0 && <p className="text-[11px] text-amber-600 mt-1">{pt ? 'Sem funcionário atribuído.' : 'No staff assigned.'}</p>}
          </div>
          <div>
            <p className="text-xs text-gray-600 flex items-center gap-1 mb-2"><Clock size={12} /> {pt ? 'Horas trabalhadas (total do serviço)' : 'Hours worked (total for the job)'}</p>
            <div className="flex items-center gap-2">
              <input type="number" min="0" value={h} onChange={e => setH(e.target.value)} className="w-20 border rounded-lg p-2 text-sm" data-testid="completed-edit-hours" /><span className="text-sm text-gray-500">h</span>
              <input type="number" min="0" max="59" value={m} onChange={e => setM(e.target.value)} className="w-20 border rounded-lg p-2 text-sm" data-testid="completed-edit-minutes" /><span className="text-sm text-gray-500">min</span>
              {staffIds.length > 1 && <span className="text-[11px] text-gray-400 ml-1">{pt ? `dividido por ${staffIds.length}` : `split across ${staffIds.length}`}</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm"><span className="text-xs text-gray-600 flex items-center gap-1"><Euro size={12} /> {pt ? 'Preço cobrado (€)' : 'Price charged (€)'}</span>
              <input type="number" step="0.01" min="0" value={price} onChange={e => setPrice(e.target.value)} className="mt-1 w-full border rounded-lg p-2 text-sm" data-testid="completed-edit-price" /></label>
            <label className="text-sm"><span className="text-xs text-gray-600">{pt ? 'Custo staff fixo (€, opcional)' : 'Fixed staff cost (€, optional)'}</span>
              <input type="number" step="0.01" min="0" value={staffCost} onChange={e => setStaffCost(e.target.value)} placeholder={pt ? 'auto (horas × taxa)' : 'auto (hours × rate)'} className="mt-1 w-full border rounded-lg p-2 text-sm" data-testid="completed-edit-staff-cost" /></label>
          </div>
          <label className="text-sm block"><span className="text-xs text-gray-600 flex items-center gap-1"><StickyNote size={12} /> {pt ? 'Notas' : 'Notes'}</span>
            <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 w-full border rounded-lg p-2 text-sm" data-testid="completed-edit-notes" /></label>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="completed-edit-error">{error}</p>}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50" data-testid="completed-edit-cancel">{pt ? 'Cancelar' : 'Cancel'}</button>
          <button onClick={save} disabled={busy} data-testid="completed-edit-save" className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-[#1a5c3a] hover:bg-[#164d31] disabled:opacity-60 text-white font-medium">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {pt ? 'Guardar' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default CompletedServiceEditModal;
